<template>
  <div class="tabs-container big-m-t big-m-b d-f jc-c p">
    <div class="w-100 d-f fd-c ai-c m-b-children" v-if="selectedLabel">
      <div class="d-f w-100">
        <div
          v-for="(tab, i) in tabs"
          :key="i"
          class="flex-1 ta-c m-b p-t p-b b-bottom link tab tt-up"
          :class="selectedTabIndex === i ? 'selected' : ''"
          @click="selectedTabIndex = i"
        >
          {{ tab }}
        </div>
      </div>

      <SubmitDemoTabInfo
        v-show="selectedTabIndex === 0"
        :song-selected="songSelected"
        :label-data="selectedLabel"
        :saving="saving"
        :show-submit-btn="tabs.length === 1"
        :initial-data="demoData"
        :pre-compressed="preCompressed"
        @submitDemo="submitDemo"
        @setSongSelected="setSongSelected"
        @original-file="originalFile = $event"
        @saveFormData="saveFormData"
      />

      <SettingsUser
        v-show="selectedTabIndex === 1"
        class="w-100"
        :for-submit-demo-modal="true"
        :saving-demo="saving"
        :submit-btn-enabled="canSubmitDemo"
        @submitDemo="submitDemo"
      />
    </div>
    <div v-else-if="!loading">
      <h2 class="p-a pos-center bc-red p">User Not Found!</h2>
    </div>

    <AuthModal :show="authModalOpen" />
    <DemoSubmissionProcessing :show="saving" />
    <div
      ref="waveformContainer"
      class="v-h w-100vw p-a t-0"
      style="left: -100vw"
    ></div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { rtdb, storage } from "@/db";
import FileUpload from "@/components/mymusic/FileUpload.vue";
import SubmitDemoTabInfo from "@/components/demos/SubmitDemoTabInfo.vue";
import { fileToBlob, sanitizedFileName } from "@/common";
import AuthModal from "@/components/settings/AuthModal.vue";
import DemoSubmissionProcessing from "@/components/demos/DemoSubmissionProcessing.vue";
import SettingsUser from "@/components/settings/SettingsUser.vue";
import WaveSurfer from "wavesurfer.js";

export default {
  name: "SubmitDemo",
  components: {
    SettingsUser,
    AuthModal,
    DemoSubmissionProcessing,
    SubmitDemoTabInfo,
    FileUpload,
  },
  data() {
    return {
      selectedLabelId: "",
      selectedLabel: null,
      selectedTabIndex: 0,
      songSelected: null,
      originalFile: null,
      preCompressed: false,
      loading: true,
      saving: false,
      authModalOpen: false,
      demoData: null,
      switchToProfileOnAuth: false,
    };
  },
  created() {
    if (this.$route.query.label) {
      this.loadLabelData(this.$route.query.label).then((data) => {
        if (data) {
          this.$root.$emit("setSelectedLabelForDemo", data);
        }
        this.loading = false;
        
        // Check for pending demo data (from drag-and-drop or return from onboarding)
        this.$nextTick(() => {
          const pendingFile = this.getPendingDemoFile;
          const pendingOriginal = this.$store.getters.getPendingDemoOriginalFile;
          const pendingData = this.getPendingDemoData;
          const pendingReturn = this.$store.getters.getPendingDemoReturn;

          if (pendingFile || pendingData || pendingReturn) {
            if (pendingFile) {
              this.preCompressed = true;
              this.setSongSelected(pendingFile);
              this.$nextTick(() => { this.preCompressed = false; });
            }
            if (pendingOriginal) {
              this.originalFile = pendingOriginal;
            }
            if (pendingData) {
              this.demoData = pendingData;
            }
            // Clear: full clear when returning from onboarding, file-only for drag-drop
            sessionStorage.removeItem("pendingDemoReturn");
            if (pendingReturn) {
              this.clearPendingDemoReturn();
            } else {
              this.clearPendingDemoFile();
            }
          }
        });
      });
    } else {
      this.loading = false;
    }

    this.$root.$on("on-close", () => {
      if (this.authModalOpen) {
        this.switchToProfileOnAuth = true;
      }
      this.closeAuthModal();
    });
  },
  methods: {
    ...mapActions(["clearPendingDemoFile", "clearPendingDemoReturn"]),
    async loadLabelData(username) {
      return this.$store
        .dispatch("resolveSettingsByUsername", username)
        .then((data) => {
          this.selectedLabel = data;
          this.selectedLabelId = data?.uid;

          return this.selectedLabel;
        });
    },
    setSongSelected(file) {
      this.songSelected = file || null;
      if (file) {
        this.selectedTabIndex = 0;
      }
    },
    saveFormData(formData) {
      // Save form data when user fills it out
      this.demoData = formData;
    },
    removeSelectedSong() {
      this.songSelected = null;
      this.selectedTabIndex = 0;
    },
    async submitDemo(_demoData) {
      // Always save form data if provided
      if (_demoData) {
        this.demoData = _demoData;
      }

      // If there's a Profile tab and we're on Info tab, switch to Profile tab
      if (this.tabs.length === 2 && this.selectedTabIndex === 0) {
        this.selectedTabIndex = 1;
        return;
      } 
      // If we're on Profile tab, use saved data (SettingsUser doesn't pass data)
      else if (this.tabs.length === 2 && this.selectedTabIndex === 1) {
        // Use saved data for Profile tab submission
        _demoData = this.demoData;
      }
      // If there's no Profile tab (tabs.length === 1), submit directly with passed data

      // Validate required fields
      if (!this.songSelected) {
        this.selectedTabIndex = 0;
        alert("Please select demo to submit.");
        return;
      }

      // Use passed data first, then fall back to saved data
      const demoData = _demoData || this.demoData;
      
      // Validate required fields - check for truthy values (not empty strings)
      if (!demoData) {
        this.selectedTabIndex = 0;
        alert("Please fill in demo information.");
        return;
      }
      
      const title = demoData.title ? demoData.title.trim() : "";
      const genreSelected = demoData.genreSelected;
      const genreValue = Array.isArray(genreSelected)
        ? genreSelected[0]
        : genreSelected;

      if (!title || !genreValue) {
        this.selectedTabIndex = 0;
        alert("Please fill in demo information.");
        return;
      }

      if (!this.user.loggedIn) {
        // Persist demo data so it survives sign-up + onboarding flow
        const returnPayload = { label: this.$route.query.label || "" };
        this.$store.dispatch("setPendingDemoFile", this.songSelected);
        this.$store.dispatch("setPendingDemoOriginalFile", this.originalFile);
        this.$store.dispatch("setPendingDemoData", demoData);
        this.$store.dispatch("setPendingDemoReturn", returnPayload);
        sessionStorage.setItem("pendingDemoReturn", JSON.stringify(returnPayload));
        this.authModalOpen = true;
        return;
      }

      this.saving = true;
      const demoSubmissionsRef = rtdb.ref(`demoSubmissions`);
      const demoId = demoSubmissionsRef.push().key;

      const { peaks, duration } = await this.generateAudioWaveform();

      try {
        const previewFileName = sanitizedFileName(title, this.songSelected.name);
        const previewSnap = await storage
          .ref(`demoSubmissions/${demoId}/${previewFileName}`)
          .put(this.songSelected);
        const url = await previewSnap.ref.getDownloadURL();

        let originalUrl = null;
        if (this.originalFile) {
          const originalFileName = sanitizedFileName(title, this.originalFile.name);
          const originalSnap = await storage
            .ref(`demoSubmissions/${demoId}/original/${originalFileName}`)
            .put(this.originalFile);
          originalUrl = await originalSnap.ref.getDownloadURL();
        }

        const demoRef = demoSubmissionsRef.child(`${demoId}`);
        const newDemo = {
          id: demoId,
          submittedBy: this.user.data.uid,
          submittedTo: this.selectedLabelId,

          title: title,
          genre: genreValue,
          description: demoData.description || "",
          url: url,
          originalUrl: originalUrl,

          timestamp: new Date().toISOString(),

          audioFileSize: this.originalFile ? this.originalFile.size : this.songSelected.size,
          status: "pending",

          peaks: JSON.stringify(peaks),
          duration,
        };
        await demoRef.set(newDemo);

        this.$store.commit("SET_DEMO_SUBMISSION_SUCCESS", {
          title: demoData.title,
          labelName: this.selectedLabel.labelName || this.selectedLabel.username,
          submittedBy: this.user.data.uid,
          submittedTo: this.selectedLabelId,
        });
        this.$router.replace({
          name: this.userType === "artist" ? "Demos" : "Inbox",
        });
      } catch (e) {
        console.error(e);
        alert("Error submitting demo. Please try again.");
      } finally {
        this.saving = false;
      }
    },
    async generateAudioWaveform() {
      if (!this.songSelected) {
        return [];
      }

      const audioBlob = await fileToBlob(this.songSelected);

      return new Promise((resolve) => {
        const waveSurfer = WaveSurfer.create({
          container: this.$refs.waveformContainer,
          backend: "WebAudio",
        });

        waveSurfer.loadBlob(audioBlob);

        waveSurfer.on("decode", (duration) => {
          const peaks = waveSurfer.exportPeaks({
            channels: 1,
          });

          resolve({ peaks, duration });
        });

        waveSurfer.on("error", () => {
          console.error("Failed to load audio for waveform generation");

          resolve({ peaks: [], duration: 0 });
        });
      });
    },
    closeAuthModal() {
      this.authModalOpen = false;
    },
  },
  computed: {
    ...mapState(["user", "userType"]),
    ...mapGetters(["getPendingDemoFile", "getPendingDemoData"]),

    tabs() {
      const tabs = ["1. Info"];

      if (this.userType !== "label") {
        tabs.push("2. Profile");
      }

      return tabs;
    },
    canSubmitDemo() {
      const g = this.demoData?.genreSelected;
      const hasGenre = Array.isArray(g)
        ? g.length > 0
        : g && (typeof g !== "string" || g.trim().length > 0);
      return (
        this.songSelected &&
        this.demoData &&
        this.demoData.title &&
        this.demoData.title.trim() &&
        hasGenre &&
        !this.saving
      );
    },
  },
  watch: {
    "user.loggedIn"(isLoggedIn) {
      if (isLoggedIn && this.switchToProfileOnAuth && this.tabs.length > 1) {
        this.switchToProfileOnAuth = false;
        this.selectedTabIndex = 1;
      }
    },
    userType() {},

    selectedTabIndex(newVal, oldVal) {
      if (newVal > 0 && !this.user.loggedIn) {
        // Persist demo data so it survives sign-up + onboarding flow
        const returnPayload = { label: this.$route.query.label || "" };
        this.$store.dispatch("setPendingDemoFile", this.songSelected);
        this.$store.dispatch("setPendingDemoOriginalFile", this.originalFile);
        this.$store.dispatch("setPendingDemoData", this.demoData);
        this.$store.dispatch("setPendingDemoReturn", returnPayload);
        sessionStorage.setItem("pendingDemoReturn", JSON.stringify(returnPayload));
        this.authModalOpen = true;

        if (newVal === 1) {
          this.selectedTabIndex = oldVal;
        }
      }
      // Save form data when switching tabs
      if (oldVal === 0 && newVal === 1 && this.$refs.submitDemoTabInfo) {
        // Trigger save by getting current form data from child component
        // The child will emit submitDemo with current data when switching
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";
</style>
